import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import faqData from "../data/faqData";
import projects from "../data/projects/projectsData";
import {
  PAGE_DESCRIPTIONS,
  PERSON_ID,
  PROJECT_PAGE_DESCRIPTIONS,
  SITE_URL,
  WEBSITE_ID,
  getProjectByPath,
  getProjectImageUrl,
} from "../data/seoMeta";

const SCRIPT_ID = "structured-data";

const buildBreadcrumbSchema = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(({ name, item }, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name,
    item,
  })),
});

// NOTE: The homepage Person + WebSite graph is emitted statically in
// public/index.html (so it is present without JS and for crawlers). It is
// intentionally NOT redefined here to avoid duplicate @id nodes on "/".
// The static block persists across client-side navigation, which also means
// the Person node stays available for author/publisher @id references on
// every subpage below.

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ProfilePage",
      "@id": `${SITE_URL}/about#webpage`,
      url: `${SITE_URL}/about`,
      name: "About Nadine (Avida2C)",
      description: PAGE_DESCRIPTIONS["/about"],
      mainEntity: { "@id": PERSON_ID },
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en",
    },
    buildBreadcrumbSchema([
      { name: "Home", item: `${SITE_URL}/` },
      { name: "About", item: `${SITE_URL}/about` },
    ]),
  ],
};

const PROJECTS_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      "@id": `${SITE_URL}/projects#webpage`,
      url: `${SITE_URL}/projects`,
      name: "Projects & Case Studies",
      description: PAGE_DESCRIPTIONS["/projects"],
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: projects.map((project, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: project.title,
          url: `${SITE_URL}${project.link}`,
        })),
      },
    },
    buildBreadcrumbSchema([
      { name: "Home", item: `${SITE_URL}/` },
      { name: "Projects", item: `${SITE_URL}/projects` },
    ]),
  ],
};

const buildFaqSchema = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ContactPage",
      "@id": `${SITE_URL}/contact#webpage`,
      url: `${SITE_URL}/contact`,
      name: "Contact Avida2C",
      description: PAGE_DESCRIPTIONS["/contact"],
      isPartOf: { "@id": WEBSITE_ID },
      inLanguage: "en",
    },
    {
      "@type": "FAQPage",
      mainEntity: faqData.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer.replace(/\n\n+/g, " "),
        },
      })),
    },
    buildBreadcrumbSchema([
      { name: "Home", item: `${SITE_URL}/` },
      { name: "Contact", item: `${SITE_URL}/contact` },
    ]),
  ],
});

const buildProjectSchema = (pathname) => {
  const project = getProjectByPath(pathname);
  if (!project) {
    return null;
  }

  const url = `${SITE_URL}${pathname}`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        "@id": `${url}#creativework`,
        name: project.title,
        description: PROJECT_PAGE_DESCRIPTIONS[pathname],
        image: getProjectImageUrl(pathname),
        url,
        author: { "@id": PERSON_ID },
        creator: { "@id": PERSON_ID },
        inLanguage: "en",
        isPartOf: { "@id": WEBSITE_ID },
      },
      buildBreadcrumbSchema([
        { name: "Home", item: `${SITE_URL}/` },
        { name: "Projects", item: `${SITE_URL}/projects` },
        { name: project.title, item: url },
      ]),
    ],
  };
};

const PROJECT_SCHEMA_BY_PATH = Object.fromEntries(
  projects.map(({ link }) => [link, () => buildProjectSchema(link)]),
);

const SCHEMA_BY_PATH = {
  // "/" is intentionally omitted. The Person + WebSite graph (HOME_SCHEMA) is
  // already emitted statically in public/index.html, so injecting it again at
  // runtime would duplicate the same @id nodes on the homepage. Runtime
  // injection only adds schema that isn't already in the static shell.
  "/about": ABOUT_SCHEMA,
  "/projects": PROJECTS_SCHEMA,
  "/contact": buildFaqSchema,
  ...PROJECT_SCHEMA_BY_PATH,
};

const StructuredData = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const existing = document.getElementById(SCRIPT_ID);
    existing?.remove();

    const schemaFactory = SCHEMA_BY_PATH[pathname];
    if (!schemaFactory) {
      return undefined;
    }

    const schema = typeof schemaFactory === "function" ? schemaFactory() : schemaFactory;
    if (!schema) {
      return undefined;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [pathname]);

  return null;
};

export default StructuredData;
